import React, { createContext, useContext, useState } from "react";
import * as courseService from "../services/courseService";
import { useNotification } from "./NotificationContext";
import axios from "axios";

// Types
export interface Course {
  _id: string;
  title: {
    en: string;
    ar: string;
  };
  description: {
    en: string;
    ar: string;
  };
  price: number;
  originalPrice: number;
  rating: number;
  students: string;
  category: string;
  level: string;
  whatYouLearn: {
    en: string[];
    ar: string[];
  };
  about: {
    en: string;
    ar: string;
  };
  language: string;
  subtitle: string;
  lastUpdate: string;
  image?: string;
  instructor?: {
    name?: string;
    bio?: {
      en: string;
      ar: string;
    };
    avatar?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCourseData {
  title?: {
    en: string;
    ar: string;
  };
  description?: {
    en: string;
    ar: string;
  };
  price?: number;
  originalPrice?: number;
  rating?: number;
  students?: string;
  category?: string;
  level?: string;
  whatYouLearn?: {
    en: string[];
    ar: string[];
  };
  about?: {
    en: string;
    ar: string;
  };
  language?: string;
  subtitle?: string;
  lastUpdate?: string;
  image?: string;
  instructor?: {
    name?: string;
    bio?: {
      en: string;
      ar: string;
    };
    avatar?: string;
  } | null;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface CourseContextType {
  courses: Course[];
  loading: boolean;
  error: string | null;
  meta: PaginationMeta | null;
  fetchCourses: (params?: any) => Promise<void>;
  createCourse: (
    courseData: Omit<Course, "_id" | "createdAt" | "updatedAt">,
    files?: { courseImage?: File; instructorAvatar?: File }
  ) => Promise<void>;
  updateCourse: (
    id: string,
    updateData: UpdateCourseData,
    files?: { courseImage?: File; instructorAvatar?: File }
  ) => Promise<void>;
  deleteCourse: (id: string) => Promise<void>;
  getCourseById: (id: string) => Promise<Course | null>;
}

const CourseContext = createContext<CourseContextType | undefined>(undefined);

export const CourseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useNotification();

  const handleApiError = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      setError(errorMessage);
      showError(errorMessage);
    } else {
      const errorMessage = "An unexpected error occurred";
      setError(errorMessage);
      showError(errorMessage);
    }
  };

  const fetchCourses = async (params = { page: 1, limit: 10 }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await courseService.getPaginatedCourses(params);
      setCourses(res.data);
      setMeta(res.meta);
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const createCourse = async (
    courseData: Omit<Course, "_id" | "createdAt" | "updatedAt">,
    files?: { courseImage?: File; instructorAvatar?: File }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const course = await courseService.createCourse(courseData, files);
      setCourses((prev) => [course, ...prev]);
      showSuccess("Course created successfully");
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const updateCourse = async (
    id: string,
    updateData: UpdateCourseData,
    files?: { courseImage?: File; instructorAvatar?: File }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await courseService.updateCourse(id, updateData, files);
      setCourses((prev) => prev.map((c) => (c._id === id ? updated : c)));
      showSuccess("Course updated successfully");
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const deleteCourse = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await courseService.deleteCourse(id);
      setCourses((prev) => prev.filter((c) => c._id !== id));
      showSuccess("Course deleted successfully");
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const getCourseById = async (id: string): Promise<Course | null> => {
    setLoading(true);
    setError(null);
    try {
      const course = await courseService.getCourseById(id);
      return course;
    } catch (e) {
      handleApiError(e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return (
    <CourseContext.Provider
      value={{
        courses,
        loading,
        error,
        meta,
        fetchCourses,
        createCourse,
        updateCourse,
        deleteCourse,
        getCourseById,
      }}
    >
      {children}
    </CourseContext.Provider>
  );
};

export const useCourse = () => {
  const context = useContext(CourseContext);
  if (!context)
    throw new Error("useCourse must be used within a CourseProvider");
  return context;
};
